// src/features/patients/prescriptions/utils/prescriptionDocument.js
//
// Monta o HTML de um receituário completo a partir do payload de
// /api/prescription/{id}/document.
//
// A ideia é sair do "despejo do editor" e produzir um documento com as partes
// que uma receita do mundo real tem:
//
//   1. Identificação do emitente (clínica ou prescritor) — omitida quando há
//      timbre, porque essa informação já está impressa na própria imagem;
//   2. Título do documento + número e data de emissão;
//   3. Identificação do paciente (nome, CPF, nascimento/idade, sexo, endereço,
//      telefone) — exigida em receituário, e obrigatória em controlados;
//   4. Hipótese diagnóstica (CID-10);
//   5. Os itens com a posologia completa: apresentação, quantidade, dose, via,
//      frequência, duração, orientações e observações;
//   6. Orientações gerais — o texto livre que o médico digitou no editor;
//   7. Local/data e o bloco de assinatura com registro profissional.
//
// Só entra no documento o que existe no sistema: campo vazio some, seção sem
// conteúdo não é impressa. Nada é inventado — em especial não afirmamos prazo
// de validade da receita, que a base não guarda.

import { sanitizePrescriptionHtml, escapeHtml } from "./prescriptionSanitize";

const DOCUMENT_TITLES = {
  medication: "RECEITUÁRIO",
  exam: "SOLICITAÇÃO DE EXAMES",
  supplement: "PRESCRIÇÃO DE SUPLEMENTOS",
  mixed: "PRESCRIÇÃO",
};

const ITEMS_SECTION_TITLES = {
  medication: "Prescrição",
  exam: "Exames solicitados",
  supplement: "Suplementos prescritos",
  mixed: "Itens prescritos",
};

const ITEM_TYPE_LABELS = {
  medication: "Medicamento",
  exam: "Exame",
  supplement: "Suplemento",
};

const MONTHS = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

// ────────────────────────────────────────────────────────────────────────────
// Formatadores
// ────────────────────────────────────────────────────────────────────────────

export function formatCpf(cpf) {
  const digits = String(cpf || "").replace(/\D/g, "");
  if (digits.length !== 11) return cpf || "";
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

export function formatCnpj(cnpj) {
  const digits = String(cnpj || "").replace(/\D/g, "");
  if (digits.length !== 14) return cnpj || "";
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

export function formatPhone(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return phone || "";
}

export function formatCep(cep) {
  const digits = String(cep || "").replace(/\D/g, "");
  if (digits.length !== 8) return cep || "";
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

/** Aceita ISO ou 'YYYY-MM-DD' e devolve um Date local (sem escorregar de dia). */
function parseDate(value) {
  if (!value) return null;
  const simple = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (simple) {
    return new Date(+simple[1], +simple[2] - 1, +simple[3]);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatDateBr(value) {
  const date = parseDate(value);
  if (!date) return "";
  return date.toLocaleDateString("pt-BR");
}

/** "12 de agosto de 2026" — como se escreve a data de emissão numa receita. */
export function formatDateLong(value) {
  const date = parseDate(value);
  if (!date) return "";
  return `${date.getDate()} de ${MONTHS[date.getMonth()]} de ${date.getFullYear()}`;
}

export function formatTimeBr(value) {
  const date = parseDate(value);
  if (!date) return "";
  return date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

export function calculateAge(birthDate) {
  const birth = parseDate(birthDate);
  if (!birth) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age >= 0 && age < 130 ? age : null;
}

export function formatGender(gender) {
  const key = String(gender || "").trim().toLowerCase();
  const map = {
    m: "Masculino", male: "Masculino", masculino: "Masculino",
    f: "Feminino", female: "Feminino", feminino: "Feminino",
    o: "Outro", other: "Outro", outro: "Outro",
  };
  return map[key] || (gender || "");
}

/** "Rua X, 123, apto 4 — Bairro, Cidade/UF — CEP 00000-000" */
export function formatAddress(address) {
  if (!address) return "";

  const line = [address.street, address.number, address.complement]
    .filter(Boolean)
    .join(", ");

  const cityPart = [address.city, address.state].filter(Boolean).join("/");
  const locality = [address.neighborhood, cityPart].filter(Boolean).join(", ");

  const parts = [line, locality].filter(Boolean);
  if (address.cep) parts.push(`CEP ${formatCep(address.cep)}`);

  return parts.join(" — ");
}

/** "CRM-SP 123456" */
export function formatRegistration(person) {
  if (!person) return "";
  const number = person.professional_registration;
  if (!number) return "";

  const type = (person.professional_registration_type || "").toUpperCase();
  const state = (person.professional_registration_state || "").toUpperCase();

  const label = [type, state].filter(Boolean).join("-");
  return label ? `${label} ${number}` : String(number);
}

// ────────────────────────────────────────────────────────────────────────────
// Texto livre do editor
// ────────────────────────────────────────────────────────────────────────────

/**
 * Devolve só o que o médico digitou À MÃO no editor, removendo os blocos de
 * item — que já são impressos de forma estruturada e completa mais acima.
 * Sem isso o documento repetiria cada medicamento duas vezes.
 */
export function extractFreeText(fullContent) {
  if (!fullContent) return "";

  const doc = new DOMParser().parseFromString(
    `<body><div id="sv-free">${fullContent}</div></body>`,
    "text/html",
  );
  const root = doc.getElementById("sv-free");
  if (!root) return "";

  root
    .querySelectorAll(".medication-line, .exam-line, .supplement-line")
    .forEach((el) => el.remove());

  const clean = sanitizePrescriptionHtml(root.innerHTML);
  return hasVisibleText(clean) ? clean : "";
}

export function hasVisibleText(html) {
  return (html || "").replace(/<[^>]*>/g, "").replace(/\s|&nbsp;/g, "").length > 0;
}

// ────────────────────────────────────────────────────────────────────────────
// Blocos do documento
// ────────────────────────────────────────────────────────────────────────────

function field(label, value) {
  if (!value) return "";
  return `<div class="doc-field"><span class="doc-field-label">${escapeHtml(
    label,
  )}</span><span class="doc-field-value">${escapeHtml(value)}</span></div>`;
}

/**
 * Seções são emitidas ACHATADAS: o título e cada filho viram irmãos no nível
 * superior do documento.
 *
 * Isso não é estética — é o que permite paginar. Se a seção fosse um único
 * elemento embrulhando 30 medicamentos, ela seria um bloco só, maior que a
 * folha, e o excedente sumiria no `overflow: hidden` da área de conteúdo.
 * Achatada, cada item é uma unidade que a paginação sabe mover de página (e o
 * título é repetido com "(cont.)" quando a seção atravessa a folha).
 */
function section(title, innerHtml) {
  if (!innerHtml) return "";
  return `<div class="doc-section-title">${escapeHtml(title)}</div>${innerHtml}`;
}

/**
 * Cabeçalho do emitente. Só entra quando NÃO há timbre — com timbre essa
 * identificação já vem impressa na imagem e duplicá-la polui a folha.
 */
function buildIssuerHeader({ clinic, prescriber }) {
  const name = clinic?.name || prescriber?.name;
  if (!name) return "";

  const details = [];
  if (clinic) {
    if (clinic.cnpj) details.push(`CNPJ ${formatCnpj(clinic.cnpj)}`);
    const address = formatAddress(clinic.address);
    if (address) details.push(address);
    const phones = (clinic.phones || []).map(formatPhone).filter(Boolean);
    if (phones.length) details.push(`Tel.: ${phones.join(" · ")}`);
  } else {
    const registration = formatRegistration(prescriber);
    if (registration) details.push(registration);
    const address = formatAddress(prescriber?.address);
    if (address) details.push(address);
    const phones = (prescriber?.phones || []).map(formatPhone).filter(Boolean);
    if (phones.length) details.push(`Tel.: ${phones.join(" · ")}`);
  }

  return `<div class="doc-issuer">
    <div class="doc-issuer-name">${escapeHtml(name)}</div>
    ${details.map((d) => `<div class="doc-issuer-line">${escapeHtml(d)}</div>`).join("")}
  </div>`;
}

function buildTitleBlock({ prescription, type }) {
  const title = DOCUMENT_TITLES[type] || DOCUMENT_TITLES.mixed;
  const number = prescription?.id ? `Nº ${prescription.id}` : "";
  const date = formatDateBr(prescription?.created_at);

  const meta = [number, date ? `Emitida em ${date}` : ""]
    .filter(Boolean)
    .join(" · ");

  return `<div class="doc-title-block">
    <div class="doc-title">${escapeHtml(title)}</div>
    ${meta ? `<div class="doc-title-meta">${escapeHtml(meta)}</div>` : ""}
  </div>`;
}

function buildPatientBlock(patient) {
  if (!patient) return "";

  const age = calculateAge(patient.birth_date);
  const birth = formatDateBr(patient.birth_date);
  const birthLabel = birth && age != null ? `${birth} (${age} anos)` : birth;

  const phones = (patient.phones || []).map(formatPhone).filter(Boolean);

  const fields = [
    field("Nome", patient.name),
    field("CPF", patient.cpf ? formatCpf(patient.cpf) : ""),
    field("Nascimento", birthLabel),
    field("Sexo", formatGender(patient.gender)),
    field("Endereço", formatAddress(patient.address)),
    field("Telefone", phones.join(" · ")),
  ]
    .filter(Boolean)
    .join("");

  return section("Identificação do paciente", fields ? `<div class="doc-fields">${fields}</div>` : "");
}

function buildConditionsBlock(conditions) {
  const list = (conditions || []).filter((c) => c?.code || c?.description);
  if (!list.length) return "";

  const items = list
    .map(
      (c) =>
        `<div class="doc-cid"><span class="doc-cid-code">${escapeHtml(
          c.code || "",
        )}</span>${c.description ? ` ${escapeHtml(c.description)}` : ""}</div>`,
    )
    .join("");

  return section("Hipótese diagnóstica (CID-10)", items);
}

/** Linha de posologia: "1 comprimido — via oral — de 12/12h — por 30 dias". */
function buildPosology(item) {
  return [
    item.dosage,
    item.administration_route ? `via ${item.administration_route}` : "",
    item.frequency,
    item.duration ? `por ${item.duration}` : "",
  ]
    .filter(Boolean)
    .join(" — ");
}

function buildItemsBlock({ items, type }) {
  if (!items?.length) return "";

  const isMixed = type === "mixed";

  const rows = items
    .map((item, index) => {
      const details = [];

      if (isMixed && item.item_type) {
        details.push(["Tipo", ITEM_TYPE_LABELS[item.item_type] || item.item_type]);
      }
      if (item.presentation) details.push(["Apresentação", item.presentation]);

      const posology = buildPosology(item);
      if (posology) details.push(["Posologia", posology]);

      if (item.instructions) details.push(["Orientações", item.instructions]);
      if (item.notes) details.push(["Observações", item.notes]);
      if (item.tuss_code) details.push(["Código TUSS", item.tuss_code]);

      const cids = (item.conditions || [])
        .map((c) => [c.code, c.description].filter(Boolean).join(" — "))
        .filter(Boolean);
      if (cids.length) details.push(["CID", cids.join("; ")]);

      const detailsHtml = details
        .map(
          ([label, value]) =>
            `<div class="doc-item-detail"><span class="doc-item-detail-label">${escapeHtml(
              label,
            )}:</span> ${escapeHtml(value)}</div>`,
        )
        .join("");

      // Quantidade sai à direita, como no receituário clássico.
      const quantity = item.quantity
        ? `<span class="doc-item-qty">${escapeHtml(item.quantity)}</span>`
        : "";

      return `<div class="doc-item">
        <div class="doc-item-head">
          <span class="doc-item-index">${index + 1}.</span>
          <span class="doc-item-name">${escapeHtml(item.medication_name || "Item sem descrição")}</span>
          ${quantity}
        </div>
        ${detailsHtml}
      </div>`;
    })
    .join("");

  return section(ITEMS_SECTION_TITLES[type] || ITEMS_SECTION_TITLES.mixed, rows);
}

function buildFreeTextBlock(freeText) {
  if (!freeText) return "";
  // Sem wrapper: os parágrafos do médico entram como blocos de nível superior
  // e podem ser quebrados entre páginas como qualquer outro conteúdo.
  return section("Orientações gerais", freeText);
}

/**
 * Local + data por extenso e o espaço de assinatura com o registro
 * profissional — sem isso a receita não vale como documento.
 */
export function buildSignatureBlock({ prescription, prescriber, clinic }) {
  const city =
    clinic?.address?.city ||
    prescriber?.address?.city ||
    "";
  const state = clinic?.address?.state || prescriber?.address?.state || "";
  const place = [city, state].filter(Boolean).join("/");

  const dateLong = formatDateLong(prescription?.created_at);
  const time = formatTimeBr(prescription?.created_at);

  const placeLine = [place, dateLong].filter(Boolean).join(", ");
  const stamp = placeLine
    ? `${placeLine}${time ? ` às ${time}` : ""}`
    : dateLong;

  const registration = formatRegistration(prescriber);

  return `<div class="doc-signature">
    ${stamp ? `<div class="doc-signature-place">${escapeHtml(stamp)}</div>` : ""}
    <div class="doc-signature-line"></div>
    <div class="doc-signature-name">${escapeHtml(prescriber?.name || "")}</div>
    ${registration ? `<div class="doc-signature-reg">${escapeHtml(registration)}</div>` : ""}
    <div class="doc-signature-hint">Assinatura e carimbo do profissional</div>
  </div>`;
}

// ────────────────────────────────────────────────────────────────────────────
// Composição
// ────────────────────────────────────────────────────────────────────────────

export function normalizeType(rawType) {
  const key = String(rawType || "").toLowerCase();
  return ["medication", "exam", "supplement", "mixed"].includes(key)
    ? key
    : "medication";
}

/**
 * Monta o documento inteiro.
 *
 * @param {Object}  documentData  payload de /api/prescription/{id}/document
 * @param {Object}  options
 * @param {boolean} options.hasLetterhead  omite o cabeçalho do emitente
 * @returns {{ bodyHtml: string, signatureHtml: string, type: string, title: string }}
 */
export function buildPrescriptionDocument(documentData, { hasLetterhead = false } = {}) {
  const {
    prescription = {},
    patient = null,
    prescriber = null,
    clinic = null,
    items = [],
    conditions = [],
  } = documentData || {};

  const type = normalizeType(prescription.prescription_type);
  const freeText = extractFreeText(prescription.full_content);

  const bodyHtml = [
    hasLetterhead ? "" : buildIssuerHeader({ clinic, prescriber }),
    buildTitleBlock({ prescription, type }),
    buildPatientBlock(patient),
    buildConditionsBlock(conditions),
    buildItemsBlock({ items, type }),
    buildFreeTextBlock(freeText),
  ]
    .filter(Boolean)
    .join("");

  return {
    bodyHtml,
    signatureHtml: buildSignatureBlock({ prescription, prescriber, clinic }),
    type,
    title: DOCUMENT_TITLES[type] || DOCUMENT_TITLES.mixed,
  };
}

/** prescricao-12-2026-08-12.pdf */
export function buildPrescriptionFileName(prescription) {
  const id = prescription?.id ?? "sem-id";
  const date = parseDate(prescription?.created_at) || new Date();
  const stamp = [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
  return `prescricao-${id}-${stamp}.pdf`;
}
